import { inject, Injectable } from '@angular/core';
import { ApiV3ServiceGetticketGet$Params } from '@api-gen/fn/service-v-2-v/api-v-3-service-getticket-get';
import { HistoryApiFrom } from '@api-gen/models/history-api-from';
import { HistoryFilterDataApiFrom } from '@api-gen/models/history-filter-data-api-from';
import { TicketInfoApiFrom } from '@api-gen/models/ticket-info-api-from';
import { ApiFromServiceV2VService } from '@api-gen/services/api-from-service-v-2-v.service';
import { ErrorResponse } from '@api/models';
import { startWithErrors } from '@core/operators/start-with-errors';
import { startWithLoading } from '@core/operators/start-with-loading';
import { StartWithUnit } from '@core/operators/start-with-unit';
import { RxState } from '@rx-angular/state';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { endOfDay, formatISO, startOfDay, subDays } from 'date-fns';
import { Observable } from 'rxjs';


export interface IHistoryState {
    historyItems: HistoryApiFrom[];
    historyFilter: HistoryFilterDataApiFrom;
    historyTicket: TicketInfoApiFrom;
    loading: Record<StartWithUnit, boolean>;
    errors: Record<StartWithUnit, ErrorResponse>;
}

export interface IHistoryAction {
    requestHistory: HistoryApiFrom[];
    setHistoryFilter: HistoryFilterDataApiFrom;
    requestHistoryTicket: TicketInfoApiFrom;
}

@Injectable()
export class HistoryState extends RxState<IHistoryState> {
    readonly #apiFromServiceV2VService: ApiFromServiceV2VService = inject(ApiFromServiceV2VService);

    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.select('loading');
    public readonly errors$: Observable<Record<StartWithUnit, ErrorResponse>> = this.select('errors');
    public readonly history$: Observable<HistoryApiFrom[]> = this.select('historyItems');
    public readonly historyFilter$: Observable<HistoryFilterDataApiFrom> = this.select('historyFilter');
    public readonly historyTicket$: Observable<TicketInfoApiFrom> = this.select('historyTicket');

    readonly #actions: RxActions<IHistoryAction> = new RxActionFactory<IHistoryAction>().create();

    constructor() {
        super();

        this.setDefaultState();
        this.connectSelectors();
    }

    public requestHistory(filter?: HistoryFilterDataApiFrom): void {
        const startDate: Date = new Date(filter.startDate);
        startDate.setTime(startDate.getTime() - new Date().getTimezoneOffset() * 60 * 1000);

        const endDate: Date = new Date(filter.endDate);
        endDate.setTime(endDate.getTime() - new Date().getTimezoneOffset() * 60 * 1000);

        const filterForApi: HistoryFilterDataApiFrom = {
            ...filter,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
        };

        this.#actions.setHistoryFilter(filter);

        this.hold(
            this.#apiFromServiceV2VService
                .apiV3ServiceHistoryPost({
                    body: filterForApi,
                })
                .pipe(startWithLoading(this, StartWithUnit.LoadHistory), startWithErrors(this, StartWithUnit.LoadHistory)),
            this.#actions.requestHistory,
        );
    }

    public requestHistoryTicket(params: ApiV3ServiceGetticketGet$Params): void {
        this.hold(
            this.#apiFromServiceV2VService
                .apiV3ServiceGetticketGet(params)
                .pipe(startWithLoading(this, StartWithUnit.LoadHistoryTicket), startWithErrors(this, StartWithUnit.LoadHistoryTicket)),
            this.#actions.requestHistoryTicket,
        );
    }

    public clearData(): void {
        this.#actions.requestHistory(null);
    }

    private setDefaultState(): void {
        this.set({
            loading: null,
            errors: null,
            historyFilter: {
                startDate: formatISO(startOfDay(subDays(new Date(), 90))),
                endDate: formatISO(endOfDay(new Date())),
                operationType: 0,
            },
            historyItems: null,
        });
    }

    private connectSelectors(): void {
        this.connect('historyItems', this.#actions.requestHistory$);
        this.connect('historyFilter', this.#actions.setHistoryFilter$);
        this.connect('historyTicket', this.#actions.requestHistoryTicket$);
    }
}
